import type React from 'react';

import { css } from '@compiled/react';

import { Button } from '@/libraries/design/components/button/src/button';
import { Link } from '@/libraries/design/components/link/src/link';

import { METER_ALLOWED_APPS, STEP_ORDER } from '../context/constants';
import { useGoToNextStep, useGoToPrevStep, useSelector, useSetStep } from '../context/hooks';
import { selectMeter, selectShareableQuery } from '../context/selectors';
import { toQueryString } from '../util/url';

import type { Step } from '../context/types';

export interface StepNavProps {
	showBack?: boolean;
	showNext?: boolean;
	showSkip?: boolean;
	nextDisabled?: boolean;
	skipTo?: Step;
	nextLabel?: string;
	/**
	 * Optional hooks that fire immediately BEFORE the corresponding
	 * navigation action. Return `false` to abort the navigation (e.g.
	 * for validation), otherwise navigation proceeds as normal.
	 *
	 * Primarily used for firing analytics events (see GWP-12102).
	 */
	onBeforeNext?: () => boolean | void;
	onBeforeBack?: () => boolean | void;
	onBeforeSkip?: () => boolean | void;
}

/**
 * Bottom navigation row for each calculator step. Renders WAC design-system
 * `Button`s so the calculator inherits the shared button styling, focus rings,
 * and accessibility behaviour.
 *
 * Layout notes:
 *  - The row is centered horizontally within the calculator container so the
 *    primary "Next" action sits in the middle of the page, per Figma.
 *  - 76px top padding separates the nav from the last question row (the
 *    "How many people are in your organization?" row).
 */
export const StepNav = (props: StepNavProps) => {
	const {
		showBack = false,
		showNext = false,
		showSkip = false,
		nextDisabled = false,
		skipTo,
		nextLabel = 'Next',
		onBeforeNext,
		onBeforeBack,
		onBeforeSkip,
	} = props;
	const prev = useGoToPrevStep();
	const next = useGoToNextStep();
	const setStep = useSetStep();
	// Build a real, valid href for the Back link — same pattern as the
	// Estimate/Summary steps' inlined Back link. The href points at the
	// calculator URL with `step` set to the previous step so the link
	// behaves as a semantically-real anchor (bookmarkable, right-
	// clickable, opens-in-new-tab). The `onClick` below prevents the
	// full page reload and drives the calculator state machine.
	const shareableQuery = useSelector(selectShareableQuery);
	const currentStep = useSelector((ctx) => ctx.step);
	const meter = useSelector(selectMeter);
	// Compute the previous step, skipping allowance for csResolutions.
	const currentIndex = STEP_ORDER.indexOf(currentStep);
	let prevStepIndex = currentIndex - 1;
	if (METER_ALLOWED_APPS[meter] === null && STEP_ORDER[prevStepIndex] === 'allowance') {
		prevStepIndex -= 1;
	}
	const prevStep: Step | undefined = STEP_ORDER[prevStepIndex];
	const prevStepHref = prevStep ? `?${toQueryString({ ...shareableQuery, step: prevStep })}` : '?';

	// Wrap the navigation action with the optional analytics hook so
	// callers can fire a "clicked" event and (optionally) abort navigation
	// by returning `false` from the hook.
	const handleBack = () => {
		if (onBeforeBack?.() === false) return;
		prev();
	};
	const handleNext = () => {
		if (onBeforeNext?.() === false) return;
		next();
	};
	const handleSkip = () => {
		if (!skipTo) return;
		if (onBeforeSkip?.() === false) return;
		setStep(skipTo);
	};

	return (
		<div css={navRowStyle}>
			{showBack && (
				// BACK: WAC design-library `<Link>` at size="md", type="primary"
				// (default anchor treatment). Renders a real `<a>` with a valid
				// href pointing to the calculator URL with `step` set to the
				// previous step — so the link is bookmarkable, right-clickable,
				// and openable in a new tab, matching normal anchor semantics.
				//
				// The inline onClick preventsDefault + drives the calculator
				// state machine to avoid a full page reload. This uses the
				// anchor-as-button pattern that `jsx-a11y/anchor-is-valid`
				// forbids by default; we suppress the rule here because:
				//   1. The href IS a valid navigation target (opens the same
				//      step in a new tab/window works exactly as expected).
				//   2. The design system doesn't yet ship a link-styled button
				//      variant; using a `<button>` would visually miss Figma.
				//   3. The visual design decision (Back looks like a text link)
				//      is sign-off'd for this Q4 calculator surface.
				//
				// Wrapper span applies extra 12px of right padding so the
				// Back link has clear visual separation from the Next button,
				// per design feedback. The base `gap: 12px` in the nav row is
				// respected on either side; the extra padding here doubles
				// that specifically after Back.
				<span css={backLinkWrapperStyle}>
					{/* eslint-disable-next-line jsx-a11y/anchor-is-valid, @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name */}
					<Link
						type="primary"
						size="md"
						text="Back"
						ariaLabel="Back"
						href={prevStepHref}
						onClick={(e: React.MouseEvent<Element>) => {
							e.preventDefault();
							handleBack();
						}}
						showArrow={false}
						showUnderline={false}
					/>
				</span>
			)}
			{showSkip && skipTo && (
				<Button variant="secondary" onClick={handleSkip}>
					Skip
				</Button>
			)}
			{showNext && (
				<Button variant="primary" isDisabled={nextDisabled} onClick={handleNext}>
					{nextLabel}
				</Button>
			)}
		</div>
	);
};

// Centered nav row with 76px top padding per Figma. Buttons flow horizontally
// with a small gap between them.
const navRowStyle = css({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	gap: '12px',
	paddingBlockStart: '32px',
	width: '100%',
});

// Wrapper around the Back link that adds extra breathing room to the
// right — 12px in addition to the nav row's existing 12px gap, for a
// total of 24px of clear space between "Back" and the primary Next
// button. Per design feedback on GWP-1XXXX (Q4 calculator polish).
const backLinkWrapperStyle = css({
	display: 'inline-flex',
	alignItems: 'center',
	paddingInlineEnd: '12px',
});
